import React from 'react';
import { Page } from '../types';
import { RocketIcon, UsersIcon } from './ui/IconComponents';

interface AboutPageProps {
    setCurrentPage: (page: Page) => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ setCurrentPage }) => {
    return (
        <div className="animate-fadeIn">
            {/* Hero Section */}
            <section className="py-24 md:py-32 relative bg-zinc-900">
                <img src="https://picsum.photos/seed/market_hero/1600/900" alt="Vibrant and colorful Nigerian market" className="absolute inset-0 w-full h-full object-cover opacity-20" />
                <div className="container mx-auto px-4 text-center relative z-10">
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
                        Our Story: From a Local Stall to a Global Mission
                    </h1>
                    <p className="mt-6 max-w-2xl mx-auto text-lg md:text-xl text-zinc-300">
                        Wampia began with a simple idea: empowering one local business to dream bigger. That idea became our passion.
                    </p>
                </div>
            </section>

            {/* The Origin Story Section */}
            <section className="py-20">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1">
                            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">The Spark of an Idea</h2>
                            <p className="mt-4 text-lg text-slate-600 dark:text-zinc-300">
                                Wampia’s journey started in a bustling local market in Nigeria. We met a talented food vendor whose delicious meals were loved by everyone in her immediate vicinity. Her challenge wasn't the quality of her food, but the limits of her reach. She dreamed of taking orders online and delivering to customers across the city, but the technology felt out of reach.
                            </p>
                            <p className="mt-4 text-slate-600 dark:text-zinc-400">
                                We saw an opportunity to help. Using the most accessible tool available—WhatsApp—we built her a simple system to take orders, answer customer questions, and manage deliveries. The result was transformative. Her business grew, she hired more staff, and her dream of scaling became a reality.
                            </p>
                        </div>
                        <div className="order-1 md:order-2">
                            <img src="https://picsum.photos/seed/vendor/800/600" className="rounded-2xl shadow-lg w-full h-full object-cover" alt="Nigerian food vendor at her street food stall" />
                        </div>
                    </div>
                </div>
            </section>

            {/* The Insight Section */}
            <section className="py-20 bg-slate-100/70 dark:bg-zinc-950">
                <div className="container mx-auto px-4 max-w-4xl text-center">
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white">A Universal Challenge</h2>
                    <p className="mt-4 text-lg text-slate-600 dark:text-zinc-300">
                        After this initial success, we looked around and saw the same story everywhere. Countless local businesses—from retailers to real estate agents—were struggling with the same problems: an inability to scale, the constant pressure to answer requests instantly, and the challenge of being "online" 24/7.
                    </p>
                    <p className="mt-4 text-lg text-slate-600 dark:text-zinc-300">
                        We realized the simple solution we built for one vendor could be the answer for millions. That's when Wampia was truly born.
                    </p>
                </div>
            </section>
            
            {/* Mission and Vision Section */}
            <section className="py-20">
                <div className="container mx-auto px-4 max-w-5xl">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="glass-effect p-8 rounded-2xl text-center border border-slate-200 dark:border-zinc-800">
                             <div className="flex justify-center items-center h-16 w-16 mx-auto bg-[#05F564]/10 rounded-full border border-[#05F564]/30">
                                <RocketIcon />
                             </div>
                            <h3 className="mt-5 text-2xl font-semibold text-slate-800 dark:text-zinc-100">Our Mission</h3>
                            <p className="mt-2 text-slate-600 dark:text-zinc-400">To empower businesses of all sizes with accessible AI-powered tools that automate communication, drive growth at scale, and create happier customers.</p>
                        </div>
                        <div className="glass-effect p-8 rounded-2xl text-center border border-slate-200 dark:border-zinc-800">
                            <div className="flex justify-center items-center h-16 w-16 mx-auto bg-[#05F564]/10 rounded-full border border-[#05F564]/30">
                                <UsersIcon />
                             </div>
                            <h3 className="mt-5 text-2xl font-semibold text-slate-800 dark:text-zinc-100">Our Vision</h3>
                            <p className="mt-2 text-slate-600 dark:text-zinc-400">We envision a world where every local business can compete in the digital economy, supported by intelligent technology that fosters growth and builds lasting customer relationships.</p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Final CTA */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="glass-effect max-w-4xl mx-auto rounded-2xl p-8 md:p-12 text-center border border-slate-200 dark:border-zinc-800">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">Join Us on Our Journey</h2>
                        <p className="mt-4 text-slate-600 dark:text-zinc-400 max-w-xl mx-auto">See how Wampia can help you write your own success story.</p>
                        <button onClick={() => setCurrentPage(Page.Pricing)} className="mt-8 bg-[#05F564] text-black font-bold px-8 py-3 rounded-lg shadow-[0_0_20px_rgba(var(--primary-green-rgb),0.3)] hover:bg-[#04d458] transition-all transform hover:scale-105">
                            View Our Plans
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;